import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useCV } from './useCV'
import { CVCanvas } from './CVCanvas'
import { Scene2DLayer } from './Scene2DLayer'
import { SceneHUD } from './SceneHUD'
import { CVControls } from './CVControls'
import { CVExplainerMobile } from './CVExplainerMobile'

gsap.registerPlugin(ScrollTrigger)

const SCENE_COUNT = 15

export function CVExplainer() {
  const scrollRef = useRef(null)
  const setScene = useCV(s => s.setScene)
  const setGlobalProgress = useCV(s => s.setGlobalProgress)
  // Mobile gets a separate continuously-scrolling layout (see CVExplainerMobile)
  // instead of the fixed-viewport GSAP cross-fade below — desktop is unchanged.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = () => setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || isMobile) return

    const sections = el.querySelectorAll('.cv-section')
    const triggers = []

    sections.forEach((section, i) => {
      const trig = ScrollTrigger.create({
        trigger: section,
        scroller: el,
        start: 'top 55%',
        end: 'bottom 45%',
        onEnter: () => setScene(i),
        onEnterBack: () => setScene(i),
      })
      triggers.push(trig)
    })

    const globalTrig = ScrollTrigger.create({
      trigger: el,
      scroller: el,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: self => setGlobalProgress(self.progress),
    })
    triggers.push(globalTrig)

    return () => {
      triggers.forEach(t => t.kill())
    }
  }, [setScene, setGlobalProgress, isMobile])

  if (isMobile) return <CVExplainerMobile />

  // 100svh (small viewport height) instead of 100vh: on mobile Safari, 100vh
  // is sized as if the address bar were hidden, so content gets clipped
  // whenever the bar is actually showing. svh always assumes the smallest
  // available viewport, so nothing ever sits behind the chrome.
  return (
    <div style={{ width: '100vw', height: '100svh', background: '#030309', overflow: 'hidden', position: 'relative' }}>
      {/* Fixed 3D canvas */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        <CVCanvas />
      </div>

      {/* 2D scene content (cross-faded) */}
      <Scene2DLayer />

      {/* HUD overlay */}
      <SceneHUD />
      <CVControls />

      {/* Invisible scroll container that drives the experience */}
      <div
        ref={scrollRef}
        style={{
          position: 'fixed', inset: 0, zIndex: 10,
          overflowY: 'scroll', overflowX: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {Array.from({ length: SCENE_COUNT }).map((_, i) => (
          <section
            key={i}
            className="cv-section"
            style={{
              height: i === 0 ? '220vh' : '160vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          />
        ))}
      </div>
    </div>
  )
}
